import PopupResponsive from '@/components/PopupResponsive';
import Tabs, { TabItemProps } from '@/components/Tabs';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import PurePrescriptionForm from './PurePrescriptionForm';
import { env } from '@/utils/env';

export type PrescriptionParamType = 'sph' | 'cyl' | 'axis' | 'add' | 'prism' | 'pd' | 'od/os';

interface LearnPrescriptionDialogProps {
  open: boolean;
  initTab?: PrescriptionParamType;
  onClose: () => void;
}

export default function LearnPrescriptionDialog({
  open,
  initTab = 'sph',
  onClose,
}: LearnPrescriptionDialogProps) {
  const presT = useTranslations('account.myPrescriptions.table');
  const t = useTranslations('account.myPrescriptions.learnPrescriptionDialog');
  const [activeParam, setActiveParam] = useState<PrescriptionParamType>('sph');

  const tabsItems = [
    {
      key: 'sph',
      label: presT('sph'),
    },
    {
      key: 'cyl',
      label: presT('cyl'),
    },
    {
      key: 'axis',
      label: presT('axis'),
    },
    {
      key: 'add',
      label: presT('add'),
    },
    {
      key: 'prism',
      label: presT('prism'),
    },
    {
      key: 'pd',
      label: presT('pd'),
    },
  ] satisfies TabItemProps[];

  const paramExplainMap = {
    sph: {
      title: t('sph.title'),
      description: t.rich('sph.description', {
        b: (chunks) => <span className="font-ib my-4 inline-block">{chunks}</span>,
        br: () => <br />,
      }),
    },
    cyl: {
      title: t('cyl.title'),
      description: t.rich('cyl.description', {
        b: (chunks) => <span className="font-ib my-4 inline-block">{chunks}</span>,
        br: () => <br />,
      }),
    },
    axis: {
      title: t('axis.title'),
      description: t.rich('axis.description', {
        b: (chunks) => <span className="font-ib my-4 inline-block">{chunks}</span>,
        br: () => <br />,
      }),
    },
    add: {
      title: t('add.title'),
      description: t.rich('add.description', {
        b: (chunks) => <span className="font-ib my-4 inline-block">{chunks}</span>,
        br: () => <br />,
      }),
    },
    prism: {
      title: t('prism.title'),
      description: t.rich('prism.description', {
        b: (chunks) => <span className="font-ib my-4 inline-block">{chunks}</span>,
        br: () => <br />,
      }),
    },
    pd: {
      title: t('pd.title'),
      description: t.rich('pd.description', {
        b: (chunks) => <span className="font-ib my-4 inline-block">{chunks}</span>,
        br: () => <br />,
        a: (chunks) => (
          <a
            href={env.NEXT_PUBLIC_PD_RULE_PDF}
            className="text-primary-purple f-be"
            target="_blank"
            rel="noreferrer"
            download="zeelool_pd_ruler.pdf"
          >
            {chunks}
          </a>
        ),
      }),
    },
  };
  const confirmClose = () => {
    onClose();
    setActiveParam('sph');
  };
  // 打开时同步到 initTab，关闭时重置为 sph，保证从外部 setInitTab + setOpen 后弹窗能正确展示对应 tab
  useEffect(() => {
    if (open) {
      setActiveParam(initTab);
    } else {
      setActiveParam('sph');
    }
  }, [open, initTab]);
  return (
    <PopupResponsive
      open={open}
      onClose={confirmClose}
      title={t('title')}
      height={'54%'}
      width={'680px'}
      sx={{
        '& .MuiDivider-root': {
          display: 'none',
        },
        '& .MuiDialogContent-root': {
          paddingTop: 0,
          paddingInline: '30px',
        },
        '& .MuiDialogTitle-root': {
          paddingBottom: '20px',
        },
        '& .MuiDialog-paper': {
          width: '680px',
          maxWidth: '680px',
        },
      }}
    >
      <div className="bg-white rounded-lg w-full relative">
        <Tabs
          items={tabsItems}
          activeKey={activeParam}
          defaultActiveKey={'sph'}
          scrollable
          onChange={(key) => setActiveParam(key as PrescriptionParamType)}
          tabsWrapperProps={{
            sx: {
              justifyContent: 'center',
            },
          }}
          sx={{
            '& .tabs-container': {
              justifyContent: 'center',
            },
            '& .MuiTabs-list': {
              gap: '40px',
            },
            '& .MuiTab-root': {
              fontSize: '16px',
            },
          }}
        />
        <PurePrescriptionForm paramValue={activeParam} />
        <section className="my-[16px] break-words">
          <div
            className="text-[16px] font-bs py-[16px]"
            dangerouslySetInnerHTML={{ __html: paramExplainMap[activeParam].title }}
          />
          <div className="text-[14px] font-br">{paramExplainMap[activeParam].description}</div>
        </section>
      </div>
    </PopupResponsive>
  );
}
